import { useCallback, useEffect, useRef, useState } from 'react'
import adminRequest from '../../utils/adminRequest'
import formatMoney, { toFiniteAmount } from '../../utils/formatMoney'

const CURRENCY_ORDER = ['UYU', 'ARS']

export const UNFREEZE_NOTE_MAX_LENGTH = 500

const UNFREEZE_ERROR_MESSAGES = {
  invalid_note: `Completá la nota (hasta ${UNFREEZE_NOTE_MAX_LENGTH} caracteres).`,
  forbidden: 'No tenés permiso para esta acción o tu sesión venció. Recargá la página.',
  uncertain: 'No pudimos confirmar si se descongeló. Revisá el saldo actualizado antes de reintentar.',
  freeze_changed: 'El congelamiento cambió mientras lo revisabas. Revisalo de nuevo.',
  failed: 'No se pudo descongelar el saldo. Reintentá.'
}

const byCurrencyOrder = (a, b) => {
  const indexA = CURRENCY_ORDER.indexOf(a.currency)
  const indexB = CURRENCY_ORDER.indexOf(b.currency)
  return (indexA === -1 ? CURRENCY_ORDER.length : indexA) - (indexB === -1 ? CURRENCY_ORDER.length : indexB)
}

const asArray = value => (Array.isArray(value) ? value.filter(item => item && typeof item === 'object') : [])

const asObject = value => (value && typeof value === 'object' && !Array.isArray(value) ? value : null)

const nonNegativeAmount = value => Math.max(0, toFiniteAmount(value) ?? 0)

const timeOf = value => {
  const time = value ? new Date(value).getTime() : NaN
  return Number.isFinite(time) ? time : 0
}

const normalizeBalance = item => {
  const balance = toFiniteAmount(item.balance)
  const held = nonNegativeAmount(item.held)
  return {
    currency: item.currency,
    balance,
    held,
    available: toFiniteAmount(item.available) ?? Math.max(0, balance - held),
    frozen: item.frozen === true
  }
}

const normalizeState = item => ({
  currency: item.currency,
  frozen: asObject(item.frozen),
  unfreezes: asArray(item.unfreezes),
  holds: asArray(item.holds).filter(hold => toFiniteAmount(hold.amount) !== null)
})

export const normalizeWallet = data => {
  const source = data && typeof data === 'object' && !Array.isArray(data) ? data : {}
  const states = asArray(source.states)
    .filter(item => typeof item.currency === 'string')
    .map(normalizeState)
    .sort(byCurrencyOrder)
  return {
    enabled: source.enabled === true,
    canPay: typeof source.can_pay === 'boolean' ? source.can_pay : null,
    frozen: source.frozen === true || states.some(state => state.frozen),
    balances: asArray(source.balances)
      .filter(item => typeof item.currency === 'string' && toFiniteAmount(item.balance) !== null)
      .map(normalizeBalance)
      .sort(byCurrencyOrder),
    pending: asArray(source.pending).filter(item => toFiniteAmount(item.amount) !== null),
    movements: asArray(source.movements),
    states
  }
}

export const positiveBalances = wallet =>
  (wallet?.balances || []).filter(item => item.balance > 0)

export const pendingTotals = wallet => {
  const totals = {}
  ;(wallet?.pending || []).forEach(item => {
    const amount = toFiniteAmount(item.amount)
    if (amount === null) return
    totals[item.currency] = (totals[item.currency] || 0) + amount
  })
  return Object.entries(totals)
    .map(([currency, amount]) => ({ currency, amount }))
    .sort(byCurrencyOrder)
}

export const walletBalanceLabels = wallet => {
  const balances = positiveBalances(wallet)
  if (!balances.length) return null
  return balances.map(item => formatMoney(item.balance, item.currency))
}

const freezeKey = frozen =>
  [frozen?.at, frozen?.reason, frozen?.transaction_id, frozen?.payment_id, frozen?.status].join('|')

export const walletFreezes = wallet => {
  const freezes = new Map()
  ;(wallet?.states || []).forEach(state => {
    if (!state.frozen) return
    const key = freezeKey(state.frozen)
    const known = freezes.get(key)
    if (known) {
      known.currencies.push(state.currency)
      return
    }
    freezes.set(key, { ...state.frozen, currencies: [state.currency] })
  })
  return [...freezes.values()]
}

export const walletHolds = wallet =>
  (wallet?.states || [])
    .flatMap(state =>
      state.holds.map(hold => ({
        id: hold._id ?? null,
        transaction_id: hold.transaction_id ?? null,
        amount: toFiniteAmount(hold.amount),
        currency: state.currency,
        created: hold.created ?? null,
        expires_at: hold.expires_at ?? null
      }))
    )
    .sort((a, b) => timeOf(b.created) - timeOf(a.created))

const unfreezeKey = unfreeze => [freezeKey(unfreeze.frozen), unfreeze.by, unfreeze.note].join('|')

export const walletUnfreezes = wallet => {
  const unfreezes = new Map()
  ;(wallet?.states || []).forEach(state =>
    state.unfreezes.forEach(unfreeze => {
      const key = unfreezeKey(unfreeze)
      const known = unfreezes.get(key)
      if (known) {
        known.currencies.push(state.currency)
        return
      }
      unfreezes.set(key, {
        at: unfreeze.at ?? null,
        by: unfreeze.by ?? null,
        note: unfreeze.note ?? null,
        frozen: asObject(unfreeze.frozen),
        currencies: [state.currency]
      })
    })
  )
  return [...unfreezes.values()].sort((a, b) => timeOf(b.at) - timeOf(a.at))
}

const unfreezeErrorOf = response => {
  if (response.networkError || response.status >= 500) return UNFREEZE_ERROR_MESSAGES.uncertain
  if (response.data?.error === 'invalid_note') return UNFREEZE_ERROR_MESSAGES.invalid_note
  if (response.status === 401 || response.status === 403) return UNFREEZE_ERROR_MESSAGES.forbidden
  return UNFREEZE_ERROR_MESSAGES.failed
}

export const unfreezeWallet = async (userId, note, frozenAt) => {
  const body = typeof frozenAt === 'string' && frozenAt ? { note, frozen_at: frozenAt } : { note }
  const response = await adminRequest(`/admin/users/wallet/${encodeURIComponent(userId)}/unfreeze`, {
    method: 'POST',
    body
  })
  if (response.ok) {
    return {
      ok: true,
      unfrozen: response.data?.unfrozen === true,
      stillFrozen: response.data?.still_frozen === true
    }
  }
  if (response.status === 409 && response.data?.error === 'freeze_changed') {
    return { ok: false, changed: true, error: UNFREEZE_ERROR_MESSAGES.freeze_changed }
  }
  return {
    ok: false,
    uncertain: Boolean(response.networkError) || response.status >= 500,
    error: unfreezeErrorOf(response)
  }
}

export const useUserWallet = (userId, enabled) => {
  const [state, setState] = useState({ loading: Boolean(enabled && userId), wallet: null, error: null })
  const latestRequest = useRef(0)

  const reload = useCallback(async () => {
    if (!enabled || !userId) return
    const requestId = latestRequest.current + 1
    latestRequest.current = requestId
    setState(previous => ({ ...previous, loading: true, error: null }))
    const result = await adminRequest(`/admin/users/wallet/${encodeURIComponent(userId)}`)
    if (requestId !== latestRequest.current) return
    if (result.ok && result.data && typeof result.data === 'object') {
      setState({ loading: false, wallet: normalizeWallet(result.data), error: null })
      return
    }
    setState(previous => ({
      loading: false,
      wallet: previous.wallet,
      error: result.status === 404 ? 'unavailable' : 'failed'
    }))
  }, [userId, enabled])

  useEffect(() => {
    reload()
    return () => {
      latestRequest.current += 1
    }
  }, [reload])

  return { ...state, reload }
}
